import type { FollowupSelectionRow } from "@repo/shared/types";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./selection-checklist.i18n";
import { useReasonText } from "./use-reason-text";

export type SelectionKey = `${"account" | "agreement"}:${string}`;

export const rowKey = (row: Pick<FollowupSelectionRow, "type" | "id">): SelectionKey =>
  `${row.type}:${row.id}`;

type SelectionChecklistProps = {
  /** Homogeneous rows of the active mode (accounts OR agreements). */
  rows: FollowupSelectionRow[];
  selected: Set<SelectionKey>;
  /** QL-567 — unlinked rows a savings plan ties to other items → where they land ("Joins X" / "New goal with …"). */
  familyHint: Map<SelectionKey, string>;
  onToggle: (key: SelectionKey, next: boolean) => void;
  disabled: boolean;
  /** QL-375 group 8 — show "Carries mandate {name}" on account rows; off when the flow's collection has mandates off. */
  showMandates: boolean;
};

/**
 * Checkbox list for the accounts / savings-plans follow-up modes: one row per
 * item with its current goal, wrapper/frequency detail, and — for ineligible
 * rows — the server-provided reason (D5) rendered unselectable.
 */
export function SelectionChecklist({
  rows,
  selected,
  familyHint,
  onToggle,
  disabled,
  showMandates,
}: SelectionChecklistProps) {
  const t = useT(T);
  const reasonText = useReasonText();
  return (
    <div className="border rounded-lg divide-y">
      {rows.map((row) => {
        const key = rowKey(row);
        const hint = row.goalName ? undefined : familyHint.get(key);
        const positionCount = row.positionCount ?? 0;
        return (
          <div
            key={key}
            className={cn(
              "flex flex-wrap items-center gap-2 px-3 py-2 text-sm",
              row.eligible ? "hover:bg-accent" : "opacity-60",
            )}
          >
            <Checkbox
              checked={selected.has(key)}
              disabled={disabled || !row.eligible}
              onCheckedChange={(checked) => onToggle(key, checked === true)}
              aria-label={row.name}
            />
            <span className="min-w-0 truncate">{row.name}</span>
            <Badge variant="outline" className="shrink-0">
              {row.type === "agreement" ? t.agreement : (row.wrapperType ?? t.account)}
            </Badge>
            <span
              className="shrink-0 text-muted-foreground text-xs truncate max-w-[10rem]"
              title={hint}
            >
              {row.goalName ?? hint ?? t.noGoal}
            </span>
            <span className="ml-auto shrink-0 text-muted-foreground text-xs text-right">
              {row.eligible
                ? row.type === "account"
                  ? `${positionCount} ${positionCount === 1 ? t.position : t.positions}`
                  : (row.frequency ?? "")
                : reasonText(row.reason ?? "")}
            </span>
            {row.eligible && row.droppedIsins && row.droppedIsins.length > 0 && (
              <span className="w-full basis-full pl-6 text-amber-600 text-xs">
                {`${t.droppedFundsWarning} ${row.droppedIsins.join(", ")}`}
              </span>
            )}
            {showMandates && row.type === "account" && row.mandateName && (
              <span className="w-full basis-full pl-6 text-muted-foreground text-xs">
                {t.carriesMandate(row.mandateName)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
